// The canvas side of Fix color's brush. Each metal colour has its own layer,
// painted in its tint for the reviewer; Apply sends it as a white PNG whose
// alpha is the mask. A colour's strokes paint its layer and clear every other
// layer, so one pixel belongs to at most one colour. The eraser paints the
// ERASE layer, the server's "no metal here".
export const ERASE = 'none'

const TINTS = { Yellow: '#ff9800', White: '#2979ff', Rose: '#ec407a' }
const FALLBACK_TINT = '#9c27b0'

export const tintFor = colour => TINTS[colour] ?? FALLBACK_TINT

export function loadImage(src) {
    return new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error('image failed to load'))
        image.src = src
    })
}

export function createLayer(width, height) {
    const layer = document.createElement('canvas')
    layer.width = width
    layer.height = height
    return layer
}

function tintedCopy(image, colour, width, height) {
    const copy = createLayer(width, height)
    const ctx = copy.getContext('2d')
    ctx.drawImage(image, 0, 0, width, height)
    ctx.globalCompositeOperation = 'source-in'
    ctx.fillStyle = tintFor(colour)
    ctx.fillRect(0, 0, width, height)
    return copy
}

const paintsLayer = (stroke, layerColour) =>
    (stroke.tool === 'paint' && stroke.colour === layerColour) || (stroke.tool === 'erase' && layerColour === ERASE)

function strokeOn(ctx, layerColour, stroke, points) {
    const paints = paintsLayer(stroke, layerColour)
    ctx.globalCompositeOperation = paints ? 'source-over' : 'destination-out'
    ctx.strokeStyle = tintFor(layerColour)
    ctx.fillStyle = tintFor(layerColour)
    ctx.lineWidth = stroke.size
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    if (points.length === 1) {
        ctx.arc(points[0].x, points[0].y, stroke.size / 2, 0, Math.PI * 2)
        ctx.fill()
        return
    }
    ctx.moveTo(points[0].x, points[0].y)
    for (const point of points.slice(1)) ctx.lineTo(point.x, point.y)
    ctx.stroke()
}

// `base` is the colour's automatic zone; `initial` the masks already painted
// on the version, every colour's (another colour's mask clears this layer).
export function renderLayer(canvas, { colour, base = null, initial = [], strokes }) {
    const ctx = canvas.getContext('2d')
    ctx.globalCompositeOperation = 'source-over'
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (base) ctx.drawImage(tintedCopy(base, colour, canvas.width, canvas.height), 0, 0)
    for (const mask of initial) {
        const own = mask.colour === colour
        ctx.globalCompositeOperation = own ? 'source-over' : 'destination-out'
        ctx.drawImage(own ? tintedCopy(mask.image, colour, canvas.width, canvas.height) : mask.image, 0, 0, canvas.width, canvas.height)
    }
    for (const stroke of strokes) {
        if (stroke.points.length) strokeOn(ctx, colour, stroke, stroke.points)
    }
    ctx.globalCompositeOperation = 'source-over'
}

export function drawSegment(canvas, colour, stroke, from, to) {
    const ctx = canvas.getContext('2d')
    strokeOn(ctx, colour, stroke, from ? [from, to] : [to])
    ctx.globalCompositeOperation = 'source-over'
}

export function maskDataUrl(layer) {
    const mask = createLayer(layer.width, layer.height)
    const ctx = mask.getContext('2d')
    ctx.drawImage(layer, 0, 0)
    ctx.globalCompositeOperation = 'source-in'
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, mask.width, mask.height)
    return mask.toDataURL('image/png')
}
